import React, { useEffect, useRef, useMemo, useCallback } from 'react';
import { FocusTrapContext } from './const';
import { FocusTrapProps, FocusTrapContextProps } from './types';
import { useFocusTrap } from './hooks';

const FocusTrap: React.FC<React.PropsWithChildren<FocusTrapProps>> = ({
	children,
	hasAutoFocus = true,
	hasDynamicContent = false,
	initialFocusTrapState = true,
	handleFocusOnTopTrap,
	handleFocusOnBottomTrap,
}) => {
	const focusTrapContainerRef = useRef(null);
	const startTrapRef = useRef<HTMLDivElement>(null);
	const endTrapRef = useRef<HTMLDivElement>(null);

	const {
		isFocusTrapEnable,
		reInitFocusTrap,
		disableFocusTrap,
		setupFocusTrap,
		lastFocusableRef,
		firstFocusableRef,
	} = useFocusTrap({
		hasAutoFocus,
	});

	useEffect(() => {
		if (focusTrapContainerRef.current) {
			setupFocusTrap(focusTrapContainerRef.current, initialFocusTrapState);
		}

		return () => {
			disableFocusTrap();
		};
	}, [setupFocusTrap, disableFocusTrap, initialFocusTrapState]);

	const mutationCallback = useCallback(() => {
		if (focusTrapContainerRef.current && hasDynamicContent) {
			reInitFocusTrap();
		}
	}, [hasDynamicContent, reInitFocusTrap]);

	useEffect(() => {
		let observer;

		if (isFocusTrapEnable && hasDynamicContent) {
			if (focusTrapContainerRef.current) {
				observer = new MutationObserver(mutationCallback);

				observer.observe(focusTrapContainerRef.current, {
					subtree: true,
					childList: true,
				});
			}
		}

		return () => {
			if (isFocusTrapEnable && hasDynamicContent && observer) {
				observer.disconnect();
			}
		};
	}, [isFocusTrapEnable, hasDynamicContent, mutationCallback]);

	const handleFocusTrapContainer = (event: React.FocusEvent) => {
		if (isFocusTrapEnable) {
			const { target } = event;

			if (target === startTrapRef.current) {
				if (lastFocusableRef.current) {
					lastFocusableRef.current.focus();
				}

				return handleFocusOnTopTrap && handleFocusOnTopTrap();
			}

			if (target === endTrapRef.current) {
				if (firstFocusableRef.current) {
					firstFocusableRef.current.focus();
				}

				return handleFocusOnBottomTrap && handleFocusOnBottomTrap();
			}
		}
	};

	const contextValue = useMemo<FocusTrapContextProps>(() => {
		return {
			reInitFocusTrap,
			disableFocusTrap,
		};
	}, [reInitFocusTrap, disableFocusTrap]);

	return (
		<div onFocus={handleFocusTrapContainer}>
			<div
				tabIndex={0}
				ref={startTrapRef}
				aria-hidden
			>
				{' '}
			</div>
			<div ref={focusTrapContainerRef}>
				<FocusTrapContext.Provider value={contextValue}>{children}</FocusTrapContext.Provider>
			</div>
			<div
				tabIndex={0}
				ref={endTrapRef}
				aria-hidden
			>
				{' '}
			</div>
		</div>
	);
};

export { FocusTrap };
